/**
 * theme/colors.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Every colour in SK Company Cash. Values are read directly out of the design
 * source (design_handoff_company_cash/SK Company Cash.dc.html) — no hex code
 * is written anywhere else in the codebase.
 *
 * The palette is deliberately monochrome: colour is reserved for STATUS, and
 * nothing else is coloured. If a new surface needs a colour that is not here,
 * the design has changed — update this file, not the component.
 * ──────────────────────────────────────────────────────────────────────────
 */

/** Neutral ramp — text, borders, surfaces. */
export const neutral = {
  /** Text, primary buttons, active nav, chart emphasis. */
  ink: '#08090A',
  /** Secondary text, form labels. */
  ink2: '#3A3A3C',
  /** Body secondary, hints, table meta. */
  grey600: '#6E6E73',
  /** Timestamps, eyebrow labels, disabled meaning. */
  grey400: '#A1A1A6',
  /** Flow-diagram arrows. */
  grey300: '#C7C7CC',
  /** Drawer action buttons. */
  borderStrong: '#D8D8D8',
  /** Every card, table and input border. */
  border: '#E4E4E4',
  /** Empty-state dashed borders. */
  borderDashed: '#CFCFCF',
  /** Grid gaps, segmented-control track, chart track. */
  ruleLight: '#F2F2F2',
  /** Custodian activity row divider. */
  ruleLighter: '#F7F7F7',
  /** Cards, page background. */
  surface: '#FFFFFF',
  /** Card headers, table headers, footers, gate bars. */
  surface2: '#FAFAFA',
} as const;

/**
 * One status family: the four values a chip / pill / dot needs. Every status
 * rendering in the app is one of these five families and nothing else, which
 * is why StatusPill takes a family name rather than four colour props.
 */
export interface StatusTone {
  border: string;
  bg: string;
  text: string;
  dot: string;
}

/**
 * The five status families. Keys are semantic tones, not domain words, because
 * one tone serves several domain states — `matched`, `active` and `approved`
 * are all `positive`. Domain-to-tone mapping lives in lib/status.ts.
 */
export const status: Record<
  'positive' | 'pending' | 'danger' | 'warning' | 'neutral' | 'plain' | 'subtle',
  StatusTone
> = {
  /** matched · active · approved · delivered */
  positive: { border: '#BFE0CE', bg: '#EEF6F1', text: '#1F6B48', dot: '#4CB782' },
  /** open · pending · waiting */
  pending: { border: '#E7DBAE', bg: '#FBF6E7', text: '#7A5F12', dot: '#DEB949' },
  /** short · rejected · failed */
  danger: { border: '#EFC9C3', bg: '#FBEEEC', text: '#96271C', dot: '#EB5757' },
  /** over · suspended · warning */
  warning: { border: '#F0D3BC', bg: '#FCF1E8', text: '#8A4212', dot: '#F0883E' },
  /** waived · informational */
  neutral: { border: '#DEDEDE', bg: '#F2F2F2', text: '#5A5A5F', dot: '#A1A1A6' },
  /** An uncoloured chip that still needs a fill — categories, instruments. */
  plain: { border: '#E4E4E4', bg: '#F7F7F7', text: '#3A3A3C', dot: '#A1A1A6' },
  /**
   * `active` in the register. Deliberately the QUIETEST family: if every
   * healthy row were green, the rows that need attention would not stand out.
   */
  subtle: { border: '#E4E4E4', bg: '#FFFFFF', text: '#3A3A3C', dot: '#A1A1A6' },
} as const;

/**
 * Gate bars — "this workspace has not been configured yet". A distinct family
 * from `warning` in the design (different border), so it is not folded in.
 * The backend signals these with HTTP 412 + `details.gate`.
 */
export const gate = {
  border: '#E8D3B0',
  bg: '#FDF7EE',
  text: '#8A4212',
} as const;

/** KPI card accent strips, in the order the Overview renders them. */
export const kpiAccent = {
  cashInCustody: neutral.ink,
  unreconciled: '#DEB949',
  openVariance: '#EB5757',
  pendingApprovals: '#F0883E',
} as const;

/** Chart series — the three assignment scopes. */
export const series = {
  open: '#4CB782',
  purpose: '#DEB949',
  collection: '#F0883E',
} as const;

/** Scrims: the drawer dims only the content area, a modal dims the page. */
export const scrim = {
  drawer: 'rgba(8,9,10,0.18)',
  modal: 'rgba(8,9,10,0.42)',
} as const;

export const colors = { ...neutral, status, gate, kpiAccent, series, scrim } as const;

export type StatusTone_ = keyof typeof status;
